/**
 * TagSelectRow Container Component
 *
 * Handles the logic for tag selection row including callback wrapping
 * and computing derived values.
 * @module features/player-kanban/components/TagSelectRow
 */

import { memo, useCallback, useMemo } from "react";

import { type Tag } from "@/features/player-kanban/types/types";

import TagSelectRowView from "./TagSelectRowView";

/**
 * Props for TagSelectRowContainer component
 */
interface TagSelectRowContainerProps {
  readonly tag: Tag & { color: string };
  readonly selectedTagIds: readonly string[];
  readonly tagUsageCounts: Record<string, number>;
  readonly onTagToggle: (tagId: string) => void;
}

/**
 * Container component for tag select row
 * Handles logic for toggling tag selection and computing display values
 * @param root0 - Component props
 * @param root0.tag - The tag to render
 * @param root0.selectedTagIds - Array of currently selected tag IDs
 * @param root0.tagUsageCounts - Record of how many players use each tag
 * @param root0.onTagToggle - Callback to toggle tag selection
 */
const TagSelectRowContainer = ({
  tag,
  selectedTagIds,
  tagUsageCounts,
  onTagToggle,
}: TagSelectRowContainerProps) => {
  const handlePress = useCallback(() => {
    onTagToggle(tag.id);
  }, [onTagToggle, tag.id]);

  const isSelected = useMemo(
    () => selectedTagIds.includes(tag.id),
    [selectedTagIds, tag.id]
  );

  const playerCount = tagUsageCounts[tag.id] || 0;

  return (
    <TagSelectRowView
      tagName={tag.name}
      tagColor={tag.color}
      isSelected={isSelected}
      playerCount={playerCount}
      onPress={handlePress}
    />
  );
};

TagSelectRowContainer.displayName = "TagSelectRowContainer";

export default memo(TagSelectRowContainer);

export type { TagSelectRowContainerProps };
